import { sportOrganizerRouteNames } from '@/routing/routeNames/sportOrganizerRouteNames'

const {
  eventDashboardSport,
  sportFloorPlanOverview,
  visitorStatisticsSport,
  scannedTicketsSport,
  accreditationsSport,
  createAccreditationSport,
  editAccreditationSport,
  addTicketSport,
  importExportSport,
  importSport,
  exportSport,
  emailsSport,
  createEmailSport,
  updateEmailSport,
  emailDetailsSport,
  scanEntriesSport,
  inviteAttendeeSport,
  ticketsSport,
  createTicketSport,
  editTicketSport,
  gratisTicketsSport,
  createGratisTicketSport,
  floorPlanSport,
  eventInfoSport,
  attendeeFieldsSport,
  sportEventWorkshops,
  newWorkshopSport,
  updateWorkshopSport,
  eventDocumentationSport,
  designWebshopTicketSport,
} = sportOrganizerRouteNames

/**
 * Sidebar keys for mapping routes to sidebar links
 * Some are string literals because for those links there isn't any route, they are just dropdowns for their subroutes
 *
 * They are in order as they are in Sidebar
 */
const sidebarKeys = {
  dashboard: eventDashboardSport,
  attendees: 'attendees',
  sportFloorPlanOverview: sportFloorPlanOverview,
  attendeeList: accreditationsSport,
  importExport: importExportSport,
  emailsToAttendees: emailsSport,
  inviteShare: 'invite-share',
  inviteAttendees: inviteAttendeeSport,
  ticketing: 'ticketing',
  tickets: ticketsSport,
  floorPlan: floorPlanSport,
  eventSettings: 'event-settings',
  eventInfo: eventInfoSport,
  attendeeFields: attendeeFieldsSport,
  visitorStatistics: visitorStatisticsSport,
  scannedTickets: scannedTicketsSport,
  eventEntries: scanEntriesSport,
  workshops: sportEventWorkshops,
  eventDocumentation: eventDocumentationSport,
  designWebshopTicketSport: designWebshopTicketSport,
}

/**
 * Sidebar links - objects for sidebar configuration
 *
 * Type:
 * {
 *    label: Name displayed in sidebar
 *    icon: Name of icon, representing the one inside assets/sidebar-icons
 *    key: Key for mapping the link to sidebar, take from sidebarKeys
 *    modulesRequired?: List of modules required for displaying this link
 *    allowedRoles?: List of roles for which this link will be displayed
 *    linkedRoutes: List of all routes for which this link will be highlighted as active and disabled for clicking (including its internal route)
 *    internalRoute: {
 *      name: Name of route this link will lead to, take from routeNames object
 *    }
 *    subroutes? List of route objects, only dropdowns have this property
 * }
 */
export const sportOrganizerSidebarConfiguration = [
  {
    label: 'Dashboard',
    icon: 'dashboard',
    key: sidebarKeys.dashboard,
    subroutes: [
      {
        label: 'Ticket statistics',
        key: sidebarKeys.dashboard,
        linkedRoutes: [eventDashboardSport],
        internalRoute: {
          name: eventDashboardSport,
        },
      },
      {
        label: 'Floor plan overview',
        key: sidebarKeys.theaterFloorPlanOverview,
        linkedRoutes: [sportFloorPlanOverview],
        internalRoute: {
          name: sportFloorPlanOverview,
        },
      },
      {
        label: 'Visitor statistics',
        key: sidebarKeys.visitorStatistics,
        linkedRoutes: [visitorStatisticsSport],
        internalRoute: {
          name: visitorStatisticsSport,
        },
      },
      {
        label: 'Scanned tickets',
        key: sidebarKeys.scannedTickets,
        linkedRoutes: [scannedTicketsSport],
        internalRoute: {
          name: scannedTicketsSport,
        },
      },
    ],
    modulesRequired: ['ticketing'],
    linkedRoutes: [eventDashboardSport],
    internalRoute: {
      name: eventDashboardSport,
    },
  },
  {
    label: 'Attendees',
    icon: 'people',
    key: sidebarKeys.attendees,
    allowedOrganizationRoles: ['OWNER', 'ADMIN', 'EVENT_ADMIN'],
    subroutes: [
      {
        label: 'Attendee list',
        key: sidebarKeys.attendeeList,
        linkedRoutes: [
          accreditationsSport,
          createAccreditationSport,
          editAccreditationSport,
          addTicketSport,
        ],
        internalRoute: {
          name: accreditationsSport,
        },
      },
      {
        label: 'Import/Export',
        key: sidebarKeys.importExport,
        linkedRoutes: [importExportSport, importSport, exportSport],
        internalRoute: {
          name: importExportSport,
        },
      },
      {
        label: 'E-mails to attendees',
        key: sidebarKeys.emailsToAttendees,
        linkedRoutes: [emailsSport, createEmailSport, updateEmailSport, emailDetailsSport],
        internalRoute: {
          name: emailsSport,
        },
      },
      {
        label: 'Event entries',
        key: sidebarKeys.eventEntries,
        modulesRequired: ['scanEntries'],
        linkedRoutes: [scanEntriesSport],
        internalRoute: {
          name: scanEntriesSport,
        },
      },
    ],
  },
  {
    label: 'Invite & share',
    icon: 'share',
    key: sidebarKeys.inviteShare,
    allowedOrganizationRoles: ['OWNER', 'ADMIN', 'EVENT_ADMIN'],
    subroutes: [
      {
        label: 'Invite attendees',
        key: sidebarKeys.inviteAttendees,
        linkedRoutes: [inviteAttendeeSport],
        internalRoute: {
          name: inviteAttendeeSport,
        },
      },
    ],
  },
  {
    label: 'Ticketing',
    icon: 'tickets',
    key: sidebarKeys.ticketing,
    allowedOrganizationRoles: ['OWNER', 'ADMIN', 'EVENT_ADMIN'],
    modulesRequired: ['ticketing'],
    subroutes: [
      {
        label: 'Tickets',
        key: sidebarKeys.tickets,
        linkedRoutes: [ticketsSport, createTicketSport, editTicketSport, gratisTicketsSport, createGratisTicketSport],
        internalRoute: {
          name: ticketsSport,
        },
      },
      {
        label: 'Floor plan',
        key: sidebarKeys.floorPlan,
        modulesRequired: ['floorPlan'],
        linkedRoutes: [floorPlanSport],
        internalRoute: {
          name: floorPlanSport,
        },
      },
      {
        label: 'Design webshop tickets',
        key: sidebarKeys.designWebshopTicketSport,
        linkedRoutes: [designWebshopTicketSport],
        internalRoute: {
          name: designWebshopTicketSport,
        },
      },
    ],
  },
  {
    label: 'Event settings',
    icon: 'settings',
    key: sidebarKeys.eventSettings,
    allowedOrganizationRoles: ['OWNER', 'ADMIN', 'EVENT_ADMIN'],
    subroutes: [
      {
        label: 'Event info',
        key: sidebarKeys.eventInfo,
        linkedRoutes: [eventInfoSport],
        internalRoute: {
          name: eventInfoSport,
        },
      },
      {
        label: 'Attendee Fields',
        key: sidebarKeys.attendeeFields,
        linkedRoutes: [attendeeFieldsSport],
        internalRoute: {
          name: attendeeFieldsSport,
        },
      },
      {
        label: 'Workshops',
        key: sidebarKeys.workshops,
        modulesRequired: ['workshops'],
        linkedRoutes: [sportEventWorkshops, newWorkshopSport, updateWorkshopSport],
        internalRoute: {
          name: sportEventWorkshops,
        },
      },
    ],
  },
  {
    label: 'Documentation',
    icon: 'documentation',
    key: sidebarKeys.eventDocumentation,
    allowedOrganizationRoles: ['OWNER', 'ADMIN', 'EVENT_ADMIN'],
    modulesRequired: ['ticketing'],
    linkedRoutes: [eventDocumentationSport],
    internalRoute: {
      name: eventDocumentationSport,
    },
  },
]
